import React, { useState, useRef, useCallback } from "react";
import { Search, MapPin, X, RefreshCw } from "lucide-react";
import { useEndPoints } from "../contexts/EndPointsContext";

const PlacesAutocomplete = ({
  onPlaceSelected,
  placeholder = "Enter a location",
  style,
}) => {
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const timeoutId = useRef(null);

  // Get endpoints from context
  const { places } = useEndPoints();

  // Debounce function
  const debounce = useCallback((func, delay) => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(func, delay);
  }, []);

  // Fetch predictions from Google Places API via backend proxy
  const fetchPredictions = useCallback(async (input) => {
    if (!input || input.length < 3) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await places.autocomplete(input);

      if (data.status === "OK") {
        setPredictions(data.predictions);
      } else if (data.status === "ZERO_RESULTS") {
        setPredictions([]);
      } else {
        setPredictions([]);
        console.log("Google Places API error:", data.status, data.error_message);
      }
    } catch (error) {
      console.error("Error fetching predictions:", error);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, [places]);

  // Get place details when a prediction is selected
  const getPlaceDetails = useCallback(async (placeId) => {
    try {
      const data = await places.details(placeId);

      if (data.status === "OK") {
        const place = data.result;
        const location = {
          address: place.formatted_address,
          latitude: place.geometry.location.lat,
          longitude: place.geometry.location.lng,
          placeId: place.place_id,
          name: place.name,
        };

        // Parse address components
        place.address_components?.forEach((component) => {
          if (component.types.includes("locality")) {
            location.city = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            location.state = component.long_name;
          }
          if (component.types.includes("country")) {
            location.country = component.long_name;
          }
          if (component.types.includes("postal_code")) {
            location.zipCode = component.long_name;
          }
        });

        onPlaceSelected(location);
        setQuery(place.formatted_address);
        setPredictions([]);
      } else {
        console.log("Google Places Details error:", data.status);
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  }, [places, onPlaceSelected]);

  // Handle text input changes
  const handleTextChange = useCallback((e) => {
    const text = e.target.value;
    setQuery(text);
    debounce(() => fetchPredictions(text), 300);
  }, [debounce, fetchPredictions]);

  // Handle prediction selection
  const handlePredictionSelect = useCallback((prediction) => {
    getPlaceDetails(prediction.place_id);
  }, [getPlaceDetails]);

  // Clear input
  const clearInput = useCallback(() => {
    setQuery("");
    setPredictions([]);
    if (onPlaceSelected) onPlaceSelected(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onPlaceSelected]);

  // Render prediction item
  const renderPrediction = useCallback((item) => (
    <div
      key={item.place_id}
      className="prediction-item"
      onClick={() => handlePredictionSelect(item)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 12px',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      <MapPin size={20} color="#666" style={{ marginRight: 12, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 16,
          color: '#333',
          marginBottom: 2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {item.structured_formatting.main_text}
        </div>
        <div style={{
          fontSize: 14,
          color: '#666',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {item.structured_formatting.secondary_text}
        </div>
      </div>
    </div>
  ), [handlePredictionSelect]);

  return (
    <div style={{ position: 'relative', width: '100%', zIndex: 1000, ...style }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${isFocused ? '#007AFF' : '#ddd'}`,
        borderRadius: '8px',
        backgroundColor: '#fff',
        padding: '0 12px',
        height: '50px',
        transition: 'border-color 0.2s',
        borderBottomLeftRadius: predictions.length > 0 ? 0 : '8px',
        borderBottomRightRadius: predictions.length > 0 ? 0 : '8px',
        borderBottom: predictions.length > 0 ? 'none' : `1px solid ${isFocused ? '#007AFF' : '#ddd'}`,
      }}>
        <Search size={20} color="#666" style={{ marginRight: 8, flexShrink: 0 }} />
        
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleTextChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          style={{
            flex: 1,
            fontSize: 16,
            color: '#333',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            height: '100%',
            padding: 0,
            fontFamily: 'inherit',
          }}
          placeholderTextColor="#999"
          autoCorrect="off"
          autoCapitalize="off"
        />

        {isLoading && (
          <RefreshCw size={20} color="#999" style={{ 
            marginRight: 4,
            animation: 'spin 1s linear infinite',
          }} />
        )}

        {query.length > 0 && !isLoading && (
          <button
            onClick={clearInput}
            style={{
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={20} color="#999" />
          </button>
        )}
      </div>

      {(predictions.length > 0 || isLoading) && (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderTop: 'none',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          maxHeight: '200px',
          overflowY: 'auto',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          {predictions.length > 0 ? (
            predictions.map(renderPrediction)
          ) : isLoading && (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: '#666',
            }}>
              Loading...
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .prediction-item:last-child {
          border-bottom: none;
        }
      `}</style>
    </div>
  );
};

export default PlacesAutocomplete;